import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Users, CheckCircle, RefreshCw, Award, GraduationCap } from 'lucide-react';
import GlowingCard from '../components/ui/GlowingCard';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3062/api';

export default function Home({ onAdminClick }) {
  const [formData, setFormData] = useState({ name: '', className: '' });
  const [generatedToken, setGeneratedToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [primaryLeaderboard, setPrimaryLeaderboard] = useState([]);
  const [secondaryLeaderboard, setSecondaryLeaderboard] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchLeaderboards();
    const interval = setInterval(fetchLeaderboards, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboards = async () => {
    try {
      setRefreshing(true);
      
      const primaryRes = await fetch(`${API_URL}/students/leaderboard?grade=primary`);
      const primaryData = await primaryRes.json();
      if (primaryData.success) {
        setPrimaryLeaderboard(primaryData.leaderboard || []);
      }

      const secondaryRes = await fetch(`${API_URL}/students/leaderboard?grade=secondary`);
      const secondaryData = await secondaryRes.json();
      if (secondaryData.success) {
        setSecondaryLeaderboard(secondaryData.leaderboard || []);
      }
    } catch (err) {
      console.error('Failed to fetch leaderboards:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.className) {
      setError('Please fill all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/students/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGeneratedToken(data.token);
        setFormData({ name: '', className: '' });
        
        setTimeout(() => {
          setGeneratedToken(null);
        }, 5000);
      } else {
        setError(data.error || 'Failed to generate token');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRankEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankClass = (rank) => {
    if (rank === 1) return 'bg-yellow-400 text-black';
    if (rank === 2) return 'bg-gray-300 text-black';
    if (rank === 3) return 'bg-amber-600 text-white';
    return 'bg-robo-blue text-robo-green';
  };

  const LeaderboardSection = ({ title, data }) => (
    <div className="space-y-3 max-h-80 overflow-y-auto">
      {data.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No scores yet</p>
          <p className="text-sm">Be the first to play!</p>
        </div>
      ) : (
        data.map((student, index) => {
          const rank = index + 1;
          return (
            <motion.div
              key={student.tokenNumber}
              className={`flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all ${
                rank <= 3 ? 'border border-yellow-400/30' : ''
              }`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getRankClass(rank)}`}>
                {getRankEmoji(rank)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate text-sm">{student.name}</p>
                <p className="text-xs text-gray-400">{student.className}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-robo-green">{student.score}</p>
              </div>
            </motion.div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-robo-dark text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-6xl font-black mb-2 bg-gradient-to-r from-robo-green via-white to-robo-green bg-clip-text text-transparent">
             HAPPY CHILDREN'S DAY ⚽
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold text-robo-green mb-4">
            ROBO SOCCER
          </h2>
          <p className="text-gray-400 text-lg">
            Get your token and compete for glory!
          </p>
        </motion.div>

        <div className="mb-8">
          <GlowingCard>
            {!generatedToken ? (
              <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-robo-green" />
                  <h3 className="text-xl font-bold">Get Your Token</h3>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-300">
                    Student Name
                  </label>
                  <input
                    type="text"
                    className="token-input"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-300">
                    Class
                  </label>
                  <select
                    className="token-input"
                    value={formData.className}
                    onChange={(e) => setFormData({ ...formData, className: e.target.value })}
                    disabled={loading}
                  >
                    <option value="">Select your class</option>
                    {[...Array(10)].map((_, i) => (
                      <option key={i + 1} value={`Grade ${i + 1}`}>
                        Grade {i + 1}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}

                <motion.button
                  type="submit"
                  className="neon-button w-full flex items-center justify-center gap-2"
                  disabled={loading}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <span>Generating...</span>
                  ) : (
                    <>
                      <Trophy className="w-5 h-5" />
                      Generate Token
                    </>
                  )}
                </motion.button>
              </form>
            ) : (
              <motion.div 
                className="text-center py-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-robo-green/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-robo-green" />
                </div>
                <h4 className="text-xl font-bold mb-2">Your Token Number</h4>
                <div className="text-6xl font-black text-robo-green mb-4">
                  #{generatedToken}
                </div>
                <p className="text-gray-400">Good luck! 🍀</p>
              </motion.div>
            )}
          </GlowingCard>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <GlowingCard>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <GraduationCap className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Primary</h3>
                  <p className="text-xs text-gray-400">Grades 1-6</p>
                </div>
              </div>
              <button 
                onClick={fetchLeaderboards}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <LeaderboardSection title="Primary" data={primaryLeaderboard} />
          </GlowingCard>

          <GlowingCard>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <GraduationCap className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Secondary</h3>
                  <p className="text-xs text-gray-400">Grades 7-10</p>
                </div>
              </div>
              <button 
                onClick={fetchLeaderboards}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <LeaderboardSection title="Secondary" data={secondaryLeaderboard} />
          </GlowingCard>
        </div>

        {/* Certificate Download Button */}
        <div className="text-center mb-4">
          <a
            href="#certificate"
            className="px-6 py-3 bg-robo-green/20 hover:bg-robo-green/30 border-2 border-robo-green rounded-xl transition-colors text-sm flex items-center gap-2 mx-auto text-white font-semibold"
          >
            <Award className="w-5 h-5" />
            Download Your Certificate
          </a>
        </div>

        <div className="text-center">
          <button
            onClick={onAdminClick}
            className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors text-sm flex items-center gap-2 mx-auto"
          >
            🔐 Admin Access
          </button>
        </div>

        <footer className="text-center mt-8 pt-6 border-t border-white/10">
          <p className="text-sm text-gray-400">
            Powered by Robo Soccer Team 🤖
          </p>
        </footer>
      </div>
    </div>
  );
}